import { Button, ComboBoxInput, TextBlock, TextInput } from 'bench-internal-theme';
import { colors } from 'bench-style-constants';
import React, { Component } from 'react';
import styled from 'styled-components';
import type { ComboBoxOptions } from 'bench-internal-theme';
import TaskOperationsPanel from './components/task-operations/TaskOperationsPanel';
import { DataEntryWithTask } from './components/task-operations/taskOperations';
import Report1099FormWrapper from './components/report-1099-form/Report1099FormWrapper';
import { Client } from './types';
import formattedCentsAmount from './formattedCentsAmount';

const editTypeOptions = [
  {
    value: 'vendorNamePrefix',
    label: 'Add Prefix',
  },
  {
    value: 'vendorNameSuffix',
    label: 'Add Suffix',
  },
  {
    value: 'vendorName',
    label: 'Replace All',
  },
];

const TransactionNameFieldsLayout = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  grid-column-gap: 2px;
`;

const BulkEditFormLayout = styled.div`
  display: grid;
  grid-gap: 12px;
  padding: 20px 32px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
`;

const BulkEditFormFieldsLayout = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 4fr 2fr 3fr 2fr;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0deda;
`;

const Report1099FormFieldsLayout = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 4fr 2fr 100px 4fr;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0deda;
`;

const BulkEditFormActions = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 1fr repeat(2, 100px);
`;

const FormGroup = styled.div`
  align-items: flex-start;
  display: grid;
  grid-gap: 4px;
`;

const UntagButton = styled(Button)`
  margin-top: 20px;
  border: none;
  background-color: ${colors.copper};
  color: ${colors.white};

  :hover {
    background-color: ${colors.pipe};
    color: ${colors.white};
  }
`;

const BulkEditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
`;

const CollapseToggleWrapper = styled.div`
  position: absolute;
  top: -25px;
  right: -12px;
  z-index: 10;
`;

const CollapsibleContent = styled.div<{ isCollapsed: boolean }>`
  display: ${({ isCollapsed }) => (isCollapsed ? 'none' : 'block')};
`;

const SectionHeader = styled(TextBlock)`
  margin-top: 20px;

  &:first-of-type {
    margin-top: 0;
  }
`;

type BulkEditFormProps = {
  dataEntriesCount: number;
  addOnOptions: Array<ComboBoxOptions>;
  ref?: React.RefObject<HTMLFormElement>;
  isSubmitting?: boolean;
  ledgerAccountOptions: Array<ComboBoxOptions>;
  onReset?: (arg: unknown) => unknown;
  client: Client;
  onUntag?: (dataEntriesBeingBulkEdited: unknown) => void;
  dataEntries?: DataEntryWithTask[];
  clientId?: string;
  personId?: string;
  isConfirmCategorizationTasksEnabled?: boolean;
  onTaskOperationComplete?: () => void;
};

type BulkEditFormState = {
  addOnComboBoxKey: number;
  editType: string;
  editTypeComboBoxKey: number;
  inputValues: Record<string, unknown>;
  ledgerAccountComboBoxKey: number;
  contractorComboBoxKey: number;
  sourceComboBoxKey: number;
  isCollapsed: boolean;
};

class BulkEditForm extends Component<BulkEditFormProps, BulkEditFormState> {
  static defaultProps = {
    dataEntriesCount: undefined,
    ref: undefined,
    isSubmitting: false,
    onReset: undefined,
  };

  constructor(props: BulkEditFormProps) {
    super(props);
    this.state = {
      addOnComboBoxKey: 0,
      editTypeComboBoxKey: 0,
      editType: 'vendorNameSuffix',
      inputValues: {},
      ledgerAccountComboBoxKey: 0,
      contractorComboBoxKey: 0,
      sourceComboBoxKey: 0,
      isCollapsed: false,
    };
  }

  handleReset = (resetEvent: any) => {
    // Forms can be natively reset via a <button type="reset">, but react-select doesn't clear the
    // visible value of the combo box when this occurs, so we need to clear the combo boxes manually
    // by rendering them with new keys. This is an alternative to setting their value since all the
    // form controls are meant to be uncontrolled.
    this.setState({
      addOnComboBoxKey: this.state.addOnComboBoxKey + 1,
      ledgerAccountComboBoxKey: this.state.ledgerAccountComboBoxKey + 1,
      contractorComboBoxKey: this.state.contractorComboBoxKey + 1,
      sourceComboBoxKey: this.state.sourceComboBoxKey + 1,
      editTypeComboBoxKey: this.state.editTypeComboBoxKey + 1,
      editType: 'vendorNameSuffix',
    });
    if (typeof this.props.onReset === 'function') {
      this.props.onReset(resetEvent);
    }
  };

  handleTextInputChange = ({ target: { name, value } }: any) => {
    const { inputValues } = this.state;
    this.setState({
      inputValues: { ...inputValues, [name]: value },
    });
  };

  handleComboBoxInputChange = (name: string) => (option: any) => {
    const { inputValues } = this.state;
    this.setState({ inputValues: { ...inputValues, [name]: option && option.value } });
  };

  handleEditTypeChange = (option: any) => {
    this.setState({
      editType: option && option.value,
    });
  };

  handleToggleCollapse = () => {
    this.setState(prevState => ({
      isCollapsed: !prevState.isCollapsed,
    }));
  };

  render() {
    const { addOnOptions, dataEntriesCount, ref, isSubmitting, ledgerAccountOptions, ...props } =
      this.props;
    const {
      addOnComboBoxKey,
      editType,
      editTypeComboBoxKey,
      ledgerAccountComboBoxKey,
      contractorComboBoxKey,
      sourceComboBoxKey,
      inputValues,
      isCollapsed,
    } = this.state;
    // Contractor needs a string value for inputValues to set `areAnyInputsFilled` to true
    if (inputValues.contractor === null) {
      inputValues.contractor = '0';
    }
    if (inputValues.source === null) {
      inputValues.source = '';
    }
    const areAnyInputsFilled = Object.values(inputValues).some(x => x);

    // Calculate debit and credit sums of selected transactions
    const { debitSum, creditSum } = props.dataEntries?.reduce(
      (acc, entry) => {
        const amount = entry.amountInCents || 0;
        if (amount >= 0) {
          acc.debitSum += amount;
        } else {
          acc.creditSum += Math.abs(amount);
        }
        return acc;
      },
      { debitSum: 0, creditSum: 0 },
    ) || { debitSum: 0, creditSum: 0 };

    const totalSum = debitSum - creditSum;
    const debitText = debitSum !== 0 ? `Debit: ${formattedCentsAmount(debitSum)}` : '';
    const creditText = creditSum !== 0 ? `Credit: ${formattedCentsAmount(creditSum)}` : '';
    const totalText = `Total: ${formattedCentsAmount(totalSum)}`;
    const amountSummary = [debitText, creditText, totalText].filter(text => text).join(', ');
    const formattedSum = amountSummary ? ` (${amountSummary})` : '';

    const selectionMessage = `${dataEntriesCount === 0 ? 'No' : dataEntriesCount} ${
      dataEntriesCount === 1 ? 'entry has' : 'entries have'
    } been selected${formattedSum}`;

    const actionsMessage = `${dataEntriesCount === 0 ? 'No' : dataEntriesCount} ${
      dataEntriesCount === 1 ? 'entry' : 'entries'
    } will be affected by these changes${formattedSum}`;

    const report1099Fields = {
      contractor: {
        key: contractorComboBoxKey,
        value: this.state.inputValues.contractor,
        onChange: this.handleComboBoxInputChange,
      },
      source: {
        key: sourceComboBoxKey,
        value: this.state.inputValues.source,
        onChange: this.handleComboBoxInputChange,
      },
    };

    return (
      <form {...props} ref={ref} onReset={this.handleReset}>
        <BulkEditFormLayout>
          <BulkEditHeader>
            <TextBlock styleType="mediumBold">Bulk Edit</TextBlock>
            <CollapseToggleWrapper>
              <Button
                type="button"
                icon={isCollapsed ? 'doubleCaretUp' : 'doubleCaretDown'}
                size="small"
                styleType="primary"
                onClick={this.handleToggleCollapse}
                title={isCollapsed ? 'Expand form' : 'Collapse form'}
              />
            </CollapseToggleWrapper>
          </BulkEditHeader>
          <CollapsibleContent isCollapsed={isCollapsed}>
            <BulkEditFormFieldsLayout>
              <FormGroup>
                <TextBlock styleType="small" uppercase>
                  Transaction Name
                </TextBlock>
                <TransactionNameFieldsLayout>
                  <ComboBoxInput
                    defaultValue={editTypeOptions.find(option => option.value === editType)}
                    key={editTypeComboBoxKey}
                    menuPlacement="top"
                    menuPortalTarget={document.body}
                    name="editType"
                    onChange={this.handleEditTypeChange}
                    options={editTypeOptions}
                  />
                  <TextInput name="vendorName" onChange={this.handleTextInputChange} />
                </TransactionNameFieldsLayout>
              </FormGroup>
              <FormGroup>
                <TextBlock styleType="small" uppercase>
                  Ledger
                </TextBlock>
                <ComboBoxInput
                  key={ledgerAccountComboBoxKey}
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name="ledgerAccountId"
                  onChange={this.handleComboBoxInputChange('ledgerAccountId')}
                  options={ledgerAccountOptions}
                />
              </FormGroup>
              <FormGroup>
                <TextBlock styleType="small" uppercase>
                  Internal Note
                </TextBlock>
                <TextInput name="internalNoteBody" onChange={this.handleTextInputChange} />
              </FormGroup>
              <FormGroup>
                <TextBlock styleType="small" uppercase>
                  Add-On
                </TextBlock>
                <ComboBoxInput
                  key={addOnComboBoxKey}
                  isClearable
                  isSearchable
                  menuPlacement="top"
                  menuPortalTarget={document.body}
                  name="addOnId"
                  onChange={this.handleComboBoxInputChange('addOnId')}
                  options={addOnOptions}
                />
              </FormGroup>
            </BulkEditFormFieldsLayout>
            {props.client && (
              <>
                <SectionHeader styleType="mediumBold">1099</SectionHeader>
                <Report1099FormFieldsLayout>
                  <Report1099FormWrapper clientId={props.client.id} fields={report1099Fields} />
                  <UntagButton onClick={this.props.onUntag}>Untag</UntagButton>
                </Report1099FormFieldsLayout>
              </>
            )}
            {props.isConfirmCategorizationTasksEnabled &&
              props.dataEntries &&
              props.clientId &&
              props.personId && (
                <>
                  <SectionHeader styleType="mediumBold">Task Operations</SectionHeader>
                  <TaskOperationsPanel
                    dataEntries={props.dataEntries}
                    clientId={props.clientId}
                    personId={props.personId}
                    isFeatureEnabled={props.isConfirmCategorizationTasksEnabled}
                    onTasksCreated={props.onTaskOperationComplete}
                    onTasksCancelled={props.onTaskOperationComplete}
                  />
                </>
              )}
          </CollapsibleContent>
          <BulkEditFormActions>
            <TextBlock color={colors.smoke} styleType="small">
              {areAnyInputsFilled ? actionsMessage : selectionMessage}
            </TextBlock>
            <Button styleType="secondary" type="reset">
              Cancel
            </Button>
            <Button
              data-heap-id="bulk-edit-apply"
              disabled={isSubmitting || !areAnyInputsFilled}
              styleType="primary"
              type="submit"
            >
              Apply
            </Button>
          </BulkEditFormActions>
        </BulkEditFormLayout>
      </form>
    );
  }
}

export default BulkEditForm;
